import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Users, MessageSquare, ArrowLeft, Plus, Send, 
  Trash2, Clock, Lock, Globe, Loader2
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import MentionInput, { MentionText } from '../components/MentionInput';

const GroupDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, api, isAdmin } = useAuth();
  const [group, setGroup] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMember, setIsMember] = useState(false);
  const [showNewPostDialog, setShowNewPostDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newPost, setNewPost] = useState({ title: '', content: '' });
  const [deleting, setDeleting] = useState(false);
  
  // For viewing a specific post
  const [selectedPost, setSelectedPost] = useState(null);
  const [postReplies, setPostReplies] = useState([]);
  const [newReply, setNewReply] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [groupRes, postsRes] = await Promise.all([
          api.get(`/groups/${id}`),
          api.get(`/groups/${id}/posts`)
        ]);
        
        setGroup(groupRes.data);
        setPosts(postsRes.data);
        
        // Check if current user is a member
        if (user && groupRes.data.members) {
          setIsMember(groupRes.data.members.some(m => m.id === user.id));
        }
      } catch (error) {
        
        navigate('/groups');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, api, user, navigate]);

  // Auto-open post from notification link (?post=xxx)
  useEffect(() => {
    const postId = searchParams.get('post');
    if (postId && posts.length > 0 && !selectedPost) {
      const post = posts.find(p => p.id === postId);
      if (post) {
        handleViewPost(post);
      }
    }
  }, [posts, searchParams, handleViewPost, selectedPost]);

  const handleJoinLeave = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      if (isMember) {
        await api.post(`/groups/${id}/leave`);
        setIsMember(false);
        setGroup(prev => ({ ...prev, member_count: prev.member_count - 1 }));
      } else {
        await api.post(`/groups/${id}/join`);
        setIsMember(true);
        setGroup(prev => ({ ...prev, member_count: prev.member_count + 1 }));
      }
    } catch (error) {
      // Non-critical error suppressed
    }
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!newPost.title.trim() || !newPost.content.trim()) return;

    setSubmitting(true);
    try {
      const res = await api.post(`/groups/${id}/posts`, {
        group_id: id,
        title: newPost.title,
        content: newPost.content
      });
      setPosts([res.data, ...posts]);
      setNewPost({ title: '', content: '' });
      setShowNewPostDialog(false);
    } catch (error) {
      // Non-critical error suppressed
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewPost = useCallback(async (post) => {
    try {
      const res = await api.get(`/groups/${id}/posts/${post.id}`);
      setSelectedPost(res.data);
      setPostReplies(res.data.replies || []);
    } catch (error) {
      // Non-critical error suppressed
    }
  }, [api, id]);

  const handleSubmitReply = async (e) => {
    e.preventDefault();
    if (!newReply.trim()) return;

    setSubmittingReply(true);
    try {
      const res = await api.post(`/groups/${id}/posts/${selectedPost.id}/replies`, {
        post_id: selectedPost.id,
        content: newReply
      });
      setPostReplies([...postReplies, res.data]);
      setNewReply('');
      
      // Update reply count in posts list
      setPosts(posts.map(p => 
        p.id === selectedPost.id ? { ...p, reply_count: p.reply_count + 1 } : p
      ));
    } catch (error) {
      // Non-critical error suppressed
    } finally {
      setSubmittingReply(false);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      await api.delete(`/groups/${id}/posts/${postId}`);
      setPosts(posts.filter(p => p.id !== postId));
      setSelectedPost(null);
    } catch (error) {
      // Non-critical error suppressed
    }
  };

  const handleDeleteGroup = async () => {
    if (!window.confirm(`Delete "${group.name}" and all its posts? This cannot be undone.`)) return;
    
    setDeleting(true);
    try {
      await api.delete(`/groups/${id}`);
      navigate('/groups');
    } catch (error) {
      
      alert(error.response?.data?.detail || 'Failed to delete group');
    } finally {
      setDeleting(false);
    }
  };

  const canReply = isMember || isAdmin;

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Skeleton className="h-48 w-full mb-8" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (!group) return null;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="group-detail-page">
      {/* Back Button */}
      <Link 
        to="/groups" 
        className="inline-flex items-center gap-2 text-text-muted hover:text-text-primary mb-8 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Groups
      </Link>

      {/* Group Header */}
      <Card className="bg-white border-brand-primary/10 mb-8 overflow-hidden">
        {group.cover_image && (
          <div className="h-48 overflow-hidden">
            <img 
              src={group.cover_image}
              alt={group.name}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <CardContent className="p-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                {group.is_private ? (
                  <Lock className="w-4 h-4 text-text-muted" />
                ) : (
                  <Globe className="w-4 h-4 text-text-muted" />
                )}
                {group.genre_focus && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-brand-primary/10 text-brand-primary">
                    {group.genre_focus}
                  </span>
                )}
              </div>
              <h1 className="font-serif text-3xl font-semibold text-text-primary">
                {group.name}
              </h1>
              <p className="text-text-secondary mt-2">{group.description}</p>
              <p className="text-sm text-text-muted mt-2 flex items-center gap-1">
                <Users className="w-4 h-4" />
                {group.member_count} members
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              {(user && (group.creator_id === user.id || isAdmin)) && (
                <Button
                  variant="destructive"
                  onClick={handleDeleteGroup}
                  disabled={deleting}
                  className="gap-2"
                  data-testid="delete-group-button"
                >
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  Delete Group
                </Button>
              )}
              <Button
                onClick={handleJoinLeave}
                className={isMember ? "btn-outline" : "btn-accent"}
                data-testid="join-leave-button"
              >
                {isMember ? 'Leave Group' : 'Join Group'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="forum">
        <TabsList className="bg-surface-alt mb-6">
          <TabsTrigger value="forum" data-testid="tab-forum">
            <MessageSquare className="w-4 h-4 mr-2" />
            Forum ({posts.length})
          </TabsTrigger>
          <TabsTrigger value="members" data-testid="tab-members">
            <Users className="w-4 h-4 mr-2" />
            Members ({group.member_count})
          </TabsTrigger>
        </TabsList>

        {/* Forum Tab */}
        <TabsContent value="forum">
          {/* New Post Button */}
          {isMember && (
            <div className="mb-6">
              <Dialog open={showNewPostDialog} onOpenChange={setShowNewPostDialog}>
                <DialogTrigger asChild>
                  <Button className="btn-accent gap-2" data-testid="new-post-button">
                    <Plus className="w-4 h-4" />
                    Start a Discussion
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Start a Discussion</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreatePost} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="title">Title</Label>
                      <Input
                        id="title"
                        placeholder="What do you want to discuss?"
                        value={newPost.title}
                        onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                        required
                        data-testid="post-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="content">Content</Label>
                      <MentionInput
                        value={newPost.content}
                        onChange={(val) => setNewPost({ ...newPost, content: val })}
                        placeholder="Share your thoughts... Use @username to mention someone"
                        rows={5}
                        data-testid="post-content-input"
                      />
                    </div>
                    <div className="flex justify-end gap-3">
                      <Button type="button" variant="outline" onClick={() => setShowNewPostDialog(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="btn-primary" disabled={submitting} data-testid="submit-post">
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Post
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          )}

          {!isMember && user && (
            <div className="bg-surface-alt rounded-lg p-6 mb-6 text-center">
              <p className="text-text-secondary">Join this group to participate in discussions</p>
            </div>
          )}

          {/* Posts List */}
          {posts.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="w-12 h-12 text-text-muted mx-auto mb-4" />
              <p className="text-text-secondary">No discussions yet. Be the first to start one!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {posts.map((post) => (
                <Card 
                  key={post.id} 
                  className="bg-white border-brand-primary/10 cursor-pointer card-hover"
                  onClick={() => handleViewPost(post)}
                  data-testid={`post-${post.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={post.author_avatar} />
                        <AvatarFallback className="bg-brand-primary text-white">
                          {post.author_name?.[0]?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <h3 className="font-semibold text-text-primary hover:text-brand-accent transition-colors">
                          {post.title}
                        </h3>
                        <p className="text-sm text-text-muted mt-1">
                          by {post.author_name} • {new Date(post.created_at).toLocaleDateString()}
                        </p>
                        <p className="text-text-secondary text-sm mt-2 line-clamp-2">
                          <MentionText text={post.content} />
                        </p>
                        <div className="flex items-center gap-4 mt-3 text-sm text-text-muted">
                          <span className="flex items-center gap-1">
                            <MessageSquare className="w-4 h-4" />
                            {post.reply_count} replies
                          </span>
                          <span className="text-brand-accent text-xs font-medium">Click to view & reply</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Members Tab */}
        <TabsContent value="members">
          <div className="grid md:grid-cols-2 gap-4">
            {group.members?.map((member) => (
              <Link 
                key={member.id} 
                to={`/writer/${member.id}`}
                data-testid={`member-${member.id}`}
              >
                <Card className="bg-white border-brand-primary/10 card-hover">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Avatar className="w-12 h-12">
                        <AvatarImage src={member.avatar_url} />
                        <AvatarFallback className="bg-brand-primary text-white">
                          {(member.display_name || member.username)?.[0]?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-text-primary">
                          {member.display_name || member.username}
                        </p>
                        <p className="text-sm text-text-muted">@{member.username}</p>
                      </div>
                      {member.id === group.creator_id && (
                        <span className="ml-auto text-xs px-2 py-1 rounded-full bg-brand-gold/20 text-brand-gold">
                          Creator
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Post Detail Modal */}
      {selectedPost && (
        <Dialog open={!!selectedPost} onOpenChange={() => setSelectedPost(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-start justify-between">
                <DialogTitle className="font-serif text-xl">{selectedPost.title}</DialogTitle>
                {(selectedPost.author_id === user?.id || user?.role === 'admin') && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-red-500"
                    onClick={() => handleDeletePost(selectedPost.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </DialogHeader>
            
            <div className="mt-4">
              <div className="flex items-center gap-3 mb-4">
                <Avatar className="w-10 h-10">
                  <AvatarImage src={selectedPost.author_avatar} />
                  <AvatarFallback className="bg-brand-primary text-white">
                    {selectedPost.author_name?.[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium text-text-primary">{selectedPost.author_name}</p>
                  <p className="text-xs text-text-muted">
                    {new Date(selectedPost.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
              
              <div className="font-reading text-text-primary leading-relaxed whitespace-pre-wrap mb-6 pb-6 border-b border-brand-primary/10">
                <MentionText text={selectedPost.content} />
              </div>

              {/* Replies */}
              <div className="space-y-4 mb-6">
                <h4 className="font-semibold text-text-primary">
                  Replies ({postReplies.length})
                </h4>
                
                {postReplies.length === 0 ? (
                  <p className="text-text-muted text-sm">No replies yet</p>
                ) : (
                  postReplies.map((reply) => (
                    <div key={reply.id} className="flex gap-3 pl-4 border-l-2 border-brand-primary/10">
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={reply.author_avatar} />
                        <AvatarFallback className="bg-brand-primary text-white text-xs">
                          {reply.author_name?.[0]?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-text-primary text-sm">{reply.author_name}</span>
                          <span className="text-xs text-text-muted">
                            {new Date(reply.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-text-secondary text-sm mt-1"><MentionText text={reply.content} /></p>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Reply Form */}
              {canReply && (
                <form onSubmit={handleSubmitReply} className="flex gap-3">
                  <div className="flex-1">
                    <MentionInput
                      value={newReply}
                      onChange={(val) => setNewReply(val)}
                      placeholder="Write a reply... Use @username to mention someone"
                      rows={2}
                      data-testid="reply-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    disabled={submittingReply || !newReply.trim()}
                    className="btn-primary self-end"
                    data-testid="submit-reply"
                  >
                    {submittingReply ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </form>
              )}
              {!canReply && user && (
                <p className="text-sm text-text-muted text-center py-2">Join this group to reply to discussions</p>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default GroupDetailPage;
